import React, { useCallback } from 'react';
import { Grid2, IconButton, Tooltip } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';
import { MODAL_TYPE } from '../../hooks/useModal';
import TranslatableTextField from '../atom/TranslatableTextField';
import RichTextEditor from '../richtexteditor/RichTextEditor';

/**
 * Marketing details section - handles duration, location, contact info, description
 */
const ShowMarketingSection = React.memo(({ values, setFieldValue, touched, errors, showModal }) => {
    const { t } = useTranslation();

    const updateMarketing = useCallback(
        (field, value) => {
            setFieldValue('marketing', { ...values.marketing, [field]: value });
        },
        [values.marketing, setFieldValue],
    );

    return (
        <>
            {/* Duration */}
            <TranslatableTextField
                type="number"
                value={values.marketing.duration || ''}
                onChange={(e) => updateMarketing('duration', Number(e.target.value))}
                label={t('shows.columns.marketing.duration')}
                gridSize={{ xs: 12, sm: 6 }}
            />

            {/* Location */}
            <TranslatableTextField
                gridSize={{ xs: 12, sm: 6 }}
                value={values.marketing.location || ''}
                onChange={(e) => updateMarketing('location', e.target.value)}
                onOpenTranslations={() =>
                    showModal(MODAL_TYPE.TRANSLATIONS, {
                        translations: values.marketing.locationI18n,
                        setTranslations: (locationI18n) =>
                            updateMarketing('locationI18n', locationI18n),
                        item: 'shows',
                        column: 'marketing.location',
                        intermediateColumn: 'columns',
                        venueId: values.venueId,
                        originalString: values.marketing.location,
                    })
                }
                label={t('shows.columns.marketing.location')}
                disabled={!values.venueId}
            />

            {/* Email */}
            <TranslatableTextField
                gridSize={{ xs: 12, sm: 6 }}
                type="email"
                value={values.marketing.email || ''}
                onChange={(e) => updateMarketing('email', e.target.value)}
                onOpenTranslations={() =>
                    showModal(MODAL_TYPE.TRANSLATIONS, {
                        translations: values.marketing.emailI18n,
                        setTranslations: (emailI18n) => updateMarketing('emailI18n', emailI18n),
                        item: 'shows',
                        column: 'marketing.email',
                        intermediateColumn: 'columns',
                        venueId: values.venueId,
                        originalString: values.marketing.email,
                    })
                }
                label={t('shows.columns.marketing.email')}
                disabled={!values.venueId}
                required
                error={touched.marketing?.email && Boolean(errors.marketing?.email)}
                helperText={touched.marketing?.email ? errors.marketing?.email : ''}
            />

            {/* Phone */}
            <TranslatableTextField
                gridSize={{ xs: 12, sm: 6 }}
                value={values.marketing.phone || ''}
                onChange={(e) => updateMarketing('phone', e.target.value)}
                onOpenTranslations={() =>
                    showModal(MODAL_TYPE.TRANSLATIONS, {
                        translations: values.marketing.phoneI18n,
                        setTranslations: (phoneI18n) => updateMarketing('phoneI18n', phoneI18n),
                        item: 'shows',
                        column: 'marketing.phone',
                        intermediateColumn: 'columns',
                        venueId: values.venueId,
                        originalString: values.marketing.phone,
                    })
                }
                label={t('shows.columns.marketing.phone')}
                disabled={!values.venueId}
            />

            {/* Description */}
            <Grid2 size={{ xs: 10, sm: 11 }}>
                <RichTextEditor
                    value={values.description}
                    onChange={(val) => setFieldValue('description', val)}
                    label={t('shows.columns.description')}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description ? errors.description : ''}
                />
            </Grid2>
            <Grid2
                size={{ xs: 2, sm: 1 }}
                sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}
            >
                <Tooltip title={t('common.translations')}>
                    <IconButton
                        onClick={() =>
                            showModal(MODAL_TYPE.RICHTEXT_TRANSLATIONS, {
                                translations: values.descriptionI18n,
                                setTranslations: (translations) =>
                                    setFieldValue('descriptionI18n', translations),
                                item: 'shows',
                                column: 'description',
                                intermediateColumn: 'columns',
                                venueId: values.venueId,
                                originalContent: values.description,
                            })
                        }
                        disabled={!values.venueId}
                        size="small"
                        edge="end"
                    >
                        <LanguageIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Grid2>
        </>
    );
});

ShowMarketingSection.displayName = 'ShowMarketingSection';

export default ShowMarketingSection;
